import {
    Grid, Box, TextField, Typography, CardHeader, Card, CardContent, debounce
} from "@mui/material";
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from "react";
import React from "react";
import MergeTagWidget from 'components/broadcast/components/broadcast-page/merge-tags';
import { EmojiPickerButton } from 'common/components/buttons';
import UrlShortenerWidget from 'components/broadcast/components/broadcast-page/url-shortener-widget';
import { FormikProps, useFormik } from "formik";
import * as Yup from "yup";
import { OpenMergeTagsWindow, MergeTagConstants } from "common/utils/merge-tag-helper";
import { AutomationConstant } from '../../api/automation-constants';
import { Editor } from "@tinymce/tinymce-react";
import { getTinyMceApiKey, MinimalTinyMCEEditor, tinyMceEditorInit } from "common/utils/shared/editor-utils";

const textEditorApiKey = getTinyMceApiKey();

const fieldNames = {
    emailSubject: "emailSubject",
    emailMessage: "emailMessage",
    isEmail: "isEmail",
} as const;

const commonTextFieldProps = {
    fullWidth: true,
    variant: "outlined" as const,
};
interface EmailSettingsProps {
    initialValues?: any;
}

const EmailSettings = React.memo(
    forwardRef<FormikProps<any>, EmailSettingsProps>((props: EmailSettingsProps, ref) => {
        const { initialValues } = props;
        const emailSubjectRef = useRef<HTMLInputElement>(null);
         const editorRef = useRef<MinimalTinyMCEEditor | null>(null);

        const validationSchema = useMemo(() => Yup.object<any>().shape<any>({

            isEmail: Yup.boolean(),
            emailSubject: Yup.string().nullable().when(fieldNames.isEmail, {
                is: true,
                then: (schema: any) => schema.required("Email subject is required")
                    .max(AutomationConstant.emailSubjectLimit, AutomationConstant.MaximumLengthAllowed(AutomationConstant.emailSubjectLimit)),
            }),
            emailMessage: Yup.string().nullable().when(fieldNames.isEmail, {
                is: true,
                then: (schema: any) => schema.required("Email message is required"),
            }),
            emailPreHeader: Yup.string().nullable().when(fieldNames.isEmail, {
                is: true,
                then: (schema: any) => schema.max(AutomationConstant.emailPreHeaderLimit, AutomationConstant.MaximumLengthAllowed(AutomationConstant.emailPreHeaderLimit)),
            })

        }), []);

        const formik = useFormik({
            initialValues: initialValues,
            validationSchema,
            enableReinitialize: true,
            onSubmit: (values) => {
            },
        });
        
        useImperativeHandle(ref, () => formik);

        const handleSubjectMergeTag = useCallback(
            (body: string) => {
                OpenMergeTagsWindow(emailSubjectRef, body, fieldNames.emailSubject, formik.setFieldValue, formik.setFieldError,AutomationConstant.emailSubjectLimit);
            },
            [formik.setFieldValue, formik.setFieldError]);

        const handleEditorChange = useCallback(
            debounce((content: any) => {
                formik.setFieldValue(fieldNames.emailMessage, content);
            }, 300),
            []
        );
        const handleMessageAppend = (insertText: string) => {
            const editor = editorRef.current;
            if (!editor) return;
            editor.focus();
            
            setTimeout(() => {
                editor.insertContent(insertText);
              }, 0);
            
          };

        return (
            <Card variant="outlined" style={{ borderColor: '#ccc', marginTop: "10px" }} sx={{ borderColor: '#ccc', mt: 1 }}>
                <CardHeader
                    title={<Typography variant="subtitle1">Email</Typography>}
                />

                <CardContent style={{ paddingTop: "0px", paddingBottom: "0px" }}>
                    <TextField
                    {...commonTextFieldProps}
                        id="email-subject"
                        inputRef={emailSubjectRef}
                       
                        size="small"
                        value={formik.values.emailSubject}
                        label="Subject"
                        name="emailSubject"
                        onChange={formik.handleChange}
                        error={!!formik.errors.emailSubject}
                        helperText={formik.errors.emailSubject}
                    />
                    <Grid item container
                        spacing={1}
                        style={{ marginLeft: "1px" }}
                    >
                        <MergeTagWidget
                            refScreen={MergeTagConstants.Automation}
                            disabled={false}
                            onClick={handleSubjectMergeTag}
                        />
                    </Grid>
                    <TextField
                        id="email-preheader"
                        {...commonTextFieldProps}
                        size="small"
                        value={formik.values.emailPreHeader}
                        label="Pre header Text"
                        name="emailPreHeader"
                        onChange={formik.handleChange}
                        error={!!formik.errors.emailPreHeader}
                        helperText={formik.errors.emailPreHeader}
                    />
                    <Box style={{ marginTop: "5px", marginBottom: "5px" }}>
                        <Editor
                            tagName="emailMessageEditor"
                            apiKey={textEditorApiKey || ''}
                            id="emailMessageEditor"
                            disabled={false}
                            value={formik.values.emailMessage}
                            onEditorChange={(content) => handleEditorChange(content)}
                            onInit={(_, editor) => {editorRef.current = editor as unknown as MinimalTinyMCEEditor;}}
                            init={tinyMceEditorInit}>
                        </Editor>
                    </Box>
                    <Grid item container spacing={1} style={{ marginLeft: "1px" }}>
                        <MergeTagWidget
                            refScreen={MergeTagConstants.Automation}
                            disabled={false}
                            onClick={handleMessageAppend}
                        />
                        <EmojiPickerButton
                            onSelect={handleMessageAppend}
                            disabled={false}
                        />
                        <UrlShortenerWidget
                            onClick={handleMessageAppend}
                            disabled={false}
                        />
                    </Grid>
                </CardContent>

            </Card>
        );
    })
);


export default EmailSettings;